import React, { useState, useRef, useCallback } from 'react'
import {
  SnackbarType,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import GlobalAlert from '@src/components/GlobalAlert'
import { useMountedState } from 'react-use'

/**
 * This hook is for displaying errors in modals and ensuring the message is
 * seen even if the modal is no longer open.
 *
 * If the modal is open when triggerModalAlert is called,
 * the error is displayed where renderModalAlert is called.
 *
 * If the modal is closed or unmounted when triggerModalAlert is called,
 * the error is displayed in a toast.
 */
export const useModalAlert = ({ isModalOpen }: { isModalOpen: boolean }) => {
  const isModalOpenRef = useRef(isModalOpen)
  isModalOpenRef.current = isModalOpen

  const isMounted = useMountedState()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const [text, setText] = useState('')

  return {
    triggerModalAlert: (text: string) => {
      if (!isMounted() || !isModalOpenRef.current) {
        setGlobalSnackbarState({
          open: true,
          message: text,
          variant: SnackbarType.Error,
        })
        return
      }
      setText(text)
    },

    resetModalAlert: useCallback(() => setText(''), []),

    renderModalAlert: ({ className }: { className?: string } = {}) => {
      if (!text) return
      return (
        <GlobalAlert severity="error" className={className}>
          {text}
        </GlobalAlert>
      )
    },
  }
}
